import { AsyncPipe, CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  ActivatedRoute,
  Router,
  RouterModule,
  RouterOutlet,
} from "@angular/router";
import { Store } from "@ngxs/store";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { ModalModule } from "ngx-bootstrap/modal";
import { Observable, Subscription, combineLatest } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { ModalComponent } from "../analytics-modal/modal.component";
import { AssignStatusComponent } from "../assign-status/assign-status.component";
import { AssignmentIndicatorComponent } from "../assignment-indicator/assignment-indicator.component";
import { LearningObjectiveViewComponent } from "../learning-objective-view/learning-objective-view.component";
import { NavbarComponent } from "../navbar/navbar.component";
import { ScrollFixAreaDirective } from "../shared/helpers/ScrollFixArea.directive";
import { AppDragFixLeftDirective } from "../shared/helpers/ScrollFixLeft.directive";
import { ScrollWithElementDirective } from "../shared/helpers/ScrollWithEl.directive";
import { ModalService } from "../shared/services/modal.service";
import { DashboardState } from "../shared/store/dashboard.state";
import {
  LearningObjectiveOrderState,
  ListData,
} from "../shared/store/learning-objective-order.state";
import {
  DeselectLearningobjectiveAction,
  LearningObjectiveData,
  LearningObjectivesState,
  SelectLearningobjectiveAction,
  SetSelectedLearningObjectiveOrderAction,
} from "../shared/store/learning-objectives.state";
import {
  FilterCandidatePlanningAction,
  PlanningFilterState,
  UndoFilterCandidatePlanningAction,
} from "../shared/store/planning-filter.state";
import {
  DataState,
  HideAssignStatusAction,
  PlanCandidateAction,
  PlanGroupAction,
  PlanningState,
  ShowAssignStatusAction,
  UnplanGroupAction,
} from "../shared/store/planning.state";
import { VirtualScrollComponent } from "../shared/virtual-scroll/virtual-scroll";
import {
  IPlanningFilters,
  IPlanningOptions,
} from "./../shared/model/interfaces";
import {
  Assignment,
  AssignmentStatus,
  Candidate,
  CitoApplicationModule,
  LearningObjective,
  LearningObjectiveVM,
  domainLoCollection,
} from "./../shared/model/model";
import { UserService } from "./../shared/services/user.service";
import {
  PlanSelectionAction,
  UnplanSelectionAction,
} from "./../shared/store/planning.state";
import { CandidateListComponent } from "./candidatelist.component";
import { FilterComponent } from "./filters.component";
import { HorizontalFilterComponent } from "./horizontal-filters.component";
import { SearchComponent } from "./search.component";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-planning",
  templateUrl: "planning.component.html",
  styleUrls: ["planning.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    RouterModule,
    RouterOutlet,
    ModalModule,
    BsDropdownModule,
    VirtualScrollComponent,
    NavbarComponent,
    HorizontalFilterComponent,
    SearchComponent,
    FilterComponent,
    CandidateListComponent,
    AssignStatusComponent,
    LearningObjectiveViewComponent,
    AssignmentIndicatorComponent,
    ModalComponent,
    ScrollWithElementDirective,
    AppDragFixLeftDirective,
    ScrollFixAreaDirective,
  ],
})
export class PlanningComponent implements OnDestroy, AfterViewInit {
  static vsScrollEnabled = true;
  @ViewChild("virtualScroll", { static: false })
  virtualScrollComponent: VirtualScrollComponent;
  groupId: string;
  groupname$: Observable<string>;
  options$: Observable<IPlanningOptions>;
  candidates$: Observable<Candidate[]>;
  uniqueYearGroups$: Observable<any>;
  uniqueClassGroups$: Observable<any>;
  filterState$: Observable<IPlanningFilters>;
  selectedLearningObjective$: Observable<any>;
  learningObjectiveData$: Observable<LearningObjectiveData>;
  learningObjectiveOrders$: Observable<ListData>;
  releaseNotes: Observable<any>;
  onboarding: any;
  onboardingDisabled: boolean;
  username: string;
  selectedLearningObjectiveOrder$: Observable<any>;
  viewPortLearningObjectives: Array<LearningObjectiveVM>;
  dataState$: Observable<DataState>;
  checkIfContentRenderedSubscription: Subscription;
  assignmentsPerLearningObjective$: Observable<{ [id: string]: Assignment[] }>;
  leftpopup: number;
  toppopup: number;
  selectedGroupName: any;
  activePage: number = 0;
  dontshowagain: boolean = false;
  visibleAssignments$: Observable<Assignment[]>;
  selectedCitoModule$: Observable<CitoApplicationModule>;
  public citomodules = CitoApplicationModule;

  @HostListener("window:resize", ["$event"])
  onResize() {
    if (this.virtualScrollComponent) {
      this.virtualScrollComponent.refresh();
    }
  }

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private modalService: ModalService,
    private router: Router,
    private store: Store
  ) {
    //this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.username = userService.user.name;
    this.groupId = this.userService.user.groupList[0];
    this.selectedCitoModule$ = this.store.select(DashboardState.citomodule);
    this.selectedLearningObjective$ = this.store.select(
      LearningObjectivesState.selectedLearningObjective
    );
    this.options$ = this.store.select(PlanningState.options);
    this.dataState$ = this.store.select(PlanningState.dataState);
    this.groupname$ = this.store.select(PlanningState.groupname);
    this.candidates$ = combineLatest([
      this.store.select(PlanningState.filteredCandidates),
      this.store.select(PlanningFilterState.filter),
    ]).pipe(
      map(([candidates, f]) =>
        candidates ? candidates?.filter(f) : candidates
      )
    );

    // get learning-objectives and get filter, sort and selected to be able to show it the right way.
    this.learningObjectiveData$ = this.store
      .select(LearningObjectivesState.learningObjectives)
      .pipe(
        map((data) => {
          return data;
        })
      );

    //Returns all the unique yearGroups from the gathered list of candidates to fill the filter on groupyears (leerjaren)
    this.uniqueYearGroups$ = this.candidates$.pipe(
      map((a) => {
        const yearGroups = Array.from(
          new Set(
            a?.map((b) => {
              return b.yearGroup;
            }) || []
          )
        );
        return yearGroups;
      })
    );

    this.uniqueClassGroups$ = this.candidates$.pipe(
      map((a) => {
        const classGroups =
          Array.from(new Set(a?.map((b) => b.groupList[0]))).map((id) => {
            return {
              id: id,
              name: a.find((n) => n.groupList[0] === id).groupName,
            };
          }) || [];
        //this.selectedGroupName = classGroups[classGroups.length - 1].name
        return classGroups;
      })
    );

    this.assignmentsPerLearningObjective$ = combineLatest([
      this.store.select(PlanningState.filteredAssignments),
      this.store.select(PlanningFilterState.filter),
    ]).pipe(
      map(([assignments, f]) => {
        if (assignments && f) {
          assignments = assignments.filter(f);
        }
        const learningObjectives = Array.from(
          new Set(assignments.map((a) => a.learningObjectiveId))
        );
        const assignmentsPerLearningObjective: { [id: string]: Assignment[] } =
          {};
        learningObjectives.forEach((l) => {
          assignmentsPerLearningObjective[l] = assignments.filter(
            (a) => a.learningObjectiveId === l
          );
        });
        return assignmentsPerLearningObjective;
      })
    );
    this.learningObjectiveOrders$ = this.store.select(
      LearningObjectiveOrderState.myLists
    );
    this.visibleAssignments$ = this.store.select(
      PlanningState.filteredAssignments
    );
  }

  ngOnInit() {
    this.userService
      .showReleaseNotes()
      .pipe(
        finalize(() => {
          !this.userService.user.hasSeenLatestReleaseNotes
            ? this.modalService.open("changelogs")
            : null;
        })
      )
      .subscribe((res) => {
        this.releaseNotes = res.html;
      });

    this.userService.showOnboarding().subscribe((res) => {
      this.onboardingDisabled = this.userService.user.onboardingDisabled;
      this.onboarding = res;
    });
  }

  ngOnDestroy(): void {
    if (this.checkIfContentRenderedSubscription) {
      this.checkIfContentRenderedSubscription.unsubscribe();
    }
  }
  ngAfterViewInit(): void {
    this.onResize();
  }
  learningObjectiveClick(le: LearningObjective, isSelected) {
    if (!isSelected) {
      if (this.route.firstChild) {
        this.showInfo(le.code);
      } else {
        this.store.dispatch(new SelectLearningobjectiveAction(le));
      }
    } else {
      this.store.dispatch(new DeselectLearningobjectiveAction());
      this.router.navigate(["plan"]);
    }
  }
  clickCell(a: Assignment, event?: MouseEvent) {
    // FIXME: Little hacky but it works, we have to take into account
    // the scrolledarea in virtualscroll.
    this.leftpopup = (event.target as any).offsetLeft;
    this.toppopup =
      (event.target as any).parentNode.parentNode.parentNode.offsetTop +
      (this.virtualScrollComponent as any)?.topPadding;
    if (a.candidateLeft) {
      switch (a.status) {
        case AssignmentStatus.Unassigned:
        case AssignmentStatus.Failed:
        case AssignmentStatus.FailedManually:
        case AssignmentStatus.Passed:
        case AssignmentStatus.PassedManually:
        case AssignmentStatus.Assigned:
        case AssignmentStatus.TestStarted:
        case AssignmentStatus.OfflineReady:
          this.store.dispatch(new ShowAssignStatusAction(a.id));
          break;
      }
    } else {
      switch (a.status) {
        case AssignmentStatus.Unassigned:
          this.store.dispatch(new HideAssignStatusAction());
          this.store.dispatch(
            new PlanCandidateAction(a.candidateId, a.learningObjectiveId)
          );
          break;
        case AssignmentStatus.Failed:
        case AssignmentStatus.FailedManually:
        case AssignmentStatus.Passed:
        case AssignmentStatus.PassedManually:
        case AssignmentStatus.Assigned:
        case AssignmentStatus.TestStarted:
        case AssignmentStatus.OfflineReady:
          this.store.dispatch(new ShowAssignStatusAction(a.id));
          break;
      }
    }
  }
  createLearningObjectiveOrder = () =>
    this.router.navigate(["/domains"], {
      queryParams: { edit: true },
    });
  showAllCandidates = () =>
    this.store.dispatch(new UndoFilterCandidatePlanningAction());
  trackEntity = (indx: number, value: any) =>
    "id" in value ? value.id : value || indx;
  showSingleCandidate = (candidateId: string) =>
    this.showCandidateProgress(candidateId);
  showCandidateProgress = (candidateId: string) =>
    this.router.navigate(
      [{ outlets: { candidateprogress: ["candidateprogress", candidateId] } }],
      { relativeTo: this.route }
    );
  filterCandidate = (candidateId: string) =>
    this.store.dispatch(new FilterCandidatePlanningAction(candidateId));
  resetCandidateFilter = () =>
    this.store.dispatch(new UndoFilterCandidatePlanningAction());
  navigateToDomains = (editMode: boolean) =>
    this.router.navigate(["/domains"], { queryParams: { edit: editMode } });
  setSelectedLearningObjectiveOrder = (id: string) =>
    this.store.dispatch(new SetSelectedLearningObjectiveOrderAction(id));
  unplanLearningObjective = (id: string) =>
    this.store.dispatch(new UnplanGroupAction(id));
  planLearningObjective = (id: string) =>
    this.store.dispatch(new PlanGroupAction(id));

  planSelection = (
    assignments: Assignment[],
    los: LearningObjective[],
    plan: boolean
  ) => {
    const assignmentsPerDomain = {};
    assignments
      .filter((a) => los.find((lo) => lo.id === a.learningObjectiveId)) //find matches
      .filter(
        (assignment, index, self) =>
          self.findIndex(
            (s) => s.learningObjectiveId === assignment.learningObjectiveId
          ) === index
      ) //remove duplicates
      .forEach((assignment) => {
        //create object with arrays per domain so we can count if any of those arrays has more than 12 entries
        const title = assignment.domainTitle.toLowerCase();
        if (assignmentsPerDomain[title]) {
          assignmentsPerDomain[title].push(assignment.learningObjectiveId);
        } else {
          assignmentsPerDomain[title] = [assignment.learningObjectiveId];
        }
      });

    const morethan12 = Object.values(assignmentsPerDomain).find(
      (domain: Assignment[]) => domain.length > 12
    ); // check if any array has more than 12 entries

    if (morethan12) {
      const alertstring = this.generateAlertString(assignmentsPerDomain);
      alert(alertstring);
    } else {
      const vals = Object.values(assignmentsPerDomain);

      if (vals) {
        const ids = Array.from(vals); // returns nested arrays
        const flattenedIds = [].concat.apply([], ids); //flatten the arrays
        if (flattenedIds.length > 0) {
          if (plan) {
            this.store.dispatch(new PlanSelectionAction(flattenedIds));
          } else {
            this.store.dispatch(new UnplanSelectionAction(flattenedIds));
          }
        }
      }
    }
  };

  generateAlertString = (aPerDomain: domainLoCollection): string => {
    let alertstring =
      "Selecteer alstublieft minder dan 12 leerdoelen per domein.\r\nOp dit moment zijn de volgende leerdoelen geselecteerd:";
    for (const [key, value] of Object.entries(aPerDomain)) {
      alertstring += `\n${key}: ${value.length} leerdoelen`;
    }
    return alertstring;
  };

  showInfo = (code: string) =>
    this.router.navigate(["learningObjective/" + code], {
      relativeTo: this.route,
    });

  showInStructure = (domainId: string, lo: LearningObjective) => {
    this.store.dispatch(new SelectLearningobjectiveAction(lo));
    this.router.navigate(["domains/" + domainId]);
  };

  openInfoPage = () => {
    this.router.navigate(["/info"]);
  };

  openModal(id: string) {
    this.modalService.open(id);
  }

  closeModal(id: string) {
    this.modalService.close(id);
  }

  prevButtonClick() {
    this.activePage--;
  }

  nextButtonClick() {
    this.activePage++;
  }

  setUserReadNotes() {
    this.userService.releasenotesRead().subscribe((res) => {
      this.userService.user.hasSeenLatestReleaseNotes = true;
    });
  }

  closeModalNotes(id: string) {
    this.modalService.close(id);
  }

  setUserSeenOnboarding() {
    this.userService.onboardingRead(this.dontshowagain).subscribe((res) => {
      console.info("User disabled onboarding on next login");
    });
  }

  closeModalOnboarding(id: string) {
    this.modalService.close(id);
    this.createLearningObjectiveOrder();
  }

  closeModalNoPages(id: string) {
    this.userService.onboardingRead(false).subscribe((res) => {});
    this.modalService.close(id);
    this.createLearningObjectiveOrder();
  }
}
